import { useEffect, useRef, useState } from "react";
import { cameraErrorMessage, openChatCamera, stopCamera } from "../../media/chat-camera";
import "./chat-camera.css";

interface Props {
  onCapture: (file: File) => void;
  onClose: () => void;
  onNativeCamera: () => void;
  onChooseFile: () => void;
}

export function ChatCamera({ onCapture, onClose, onNativeCamera, onChooseFile }: Props) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stopRef = useRef<() => void>(() => {});
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => { if (dialog?.open) dialog.close(); };
  }, []);

  useEffect(() => {
    const abort = new AbortController();
    let alive = true;
    const video = videoRef.current;
    let previewTimer: ReturnType<typeof setTimeout> | undefined;
    const stop = () => {
      alive = false;
      abort.abort();
      clearTimeout(previewTimer);
      stopCamera(streamRef.current);
      streamRef.current = null;
      if (video) video.srcObject = null;
    };
    stopRef.current = stop;
    const loseCamera = () => {
      if (!alive) return;
      setReady(false);
      setError("La cámara se ha desconectado. Puedes reintentarlo o adjuntar una foto.");
      stop();
    };
    const hide = () => {
      if (!document.hidden) return;
      setReady(false);
      setError("La cámara se ha pausado. Pulsa Reintentar cámara para continuar.");
      stop();
    };
    const previewReady = () => clearTimeout(previewTimer);
    video?.addEventListener("loadeddata", previewReady);
    document.addEventListener("visibilitychange", hide);
    setReady(false);
    setError("");
    void (async () => {
      try {
        const stream = await openChatCamera(navigator.mediaDevices, abort.signal);
        if (!alive) { stopCamera(stream); return; }
        streamRef.current = stream;
        stream.getVideoTracks().forEach(track => track.addEventListener("ended", loseCamera, { once: true }));
        if (!video) { stop(); return; }
        video.srcObject = stream;
        previewTimer = setTimeout(() => {
          if (!alive) return;
          setReady(false); setError("No se pudo mostrar la cámara. Puedes reintentar o adjuntar una foto."); stop();
        }, 15000);
        await video.play();
      } catch (error) {
        if (!alive) return;
        setReady(false); setError(cameraErrorMessage(error)); stop();
      }
    })();
    return () => {
      document.removeEventListener("visibilitychange", hide);
      video?.removeEventListener("loadeddata", previewReady);
      stop();
    };
  }, [attempt]);

  function close(action = onClose) {
    stopRef.current();
    dialogRef.current?.close();
    action();
  }

  async function capture() {
    const video = videoRef.current;
    if (!ready || busy || !video?.videoWidth || !video.videoHeight) return;
    setBusy(true);
    setError("");
    try {
      const scale = Math.min(1, 2048 / Math.max(video.videoWidth, video.videoHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
      canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
      const context = canvas.getContext("2d");
      if (!context) throw new Error("No se pudo capturar la foto.");
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error("La captura tardó demasiado.")), 10000);
        canvas.toBlob(value => {
          clearTimeout(timer);
          if (value) resolve(value); else reject(new Error("No se pudo crear la foto."));
        }, "image/jpeg", 0.9);
      });
      // The dialog may have been cancelled while the browser was encoding.
      if (!streamRef.current) return;
      const file = new File([blob], "foto-chat.jpg", { type: "image/jpeg" });
      stopRef.current();
      onCapture(file);
    } catch (error) {
      if (streamRef.current) setError(error instanceof Error ? error.message : "No se pudo capturar la foto.");
    } finally { setBusy(false); }
  }

  return <dialog ref={dialogRef} className="chat-camera" aria-labelledby="chat-camera-title"
    onCancel={event => { event.preventDefault(); close(); }}>
    <h3 id="chat-camera-title">Cámara del chat</h3>
    <p>Haz una foto para adjuntarla a tu mensaje.</p>
    <video ref={videoRef} autoPlay muted playsInline aria-label="Vista de la cámara"
      onLoadedData={() => setReady(Boolean(streamRef.current && videoRef.current?.videoWidth))}
      onError={() => { setReady(false); setError("No se pudo mostrar la cámara."); stopRef.current(); }} />
    {error ? <p role="alert">{error}</p> : !ready ? <p role="status">Abriendo cámara…</p> : null}
    <div className="chat-camera-actions">
      <button className="primary-button" type="button" disabled={!ready || busy} onClick={() => void capture()}>
        {busy ? "Preparando foto…" : "Tomar foto"}
      </button>
      {error ? <button className="secondary-button" type="button" onClick={() => setAttempt(value => value + 1)}>Reintentar cámara</button> : null}
      <button className="secondary-button" type="button" onClick={() => close(onNativeCamera)}>Cámara del dispositivo</button>
      <button className="secondary-button" type="button" onClick={() => close(onChooseFile)}>Elegir una imagen</button>
      <button className="secondary-button" type="button" onClick={() => close()}>Cancelar</button>
    </div>
  </dialog>;
}
